import {
	getUserDetails,
	updateUserDetails,
	uploadFiles,
} from 'util/api'
import { getCache, setCache, verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { showToast } from 'components/toast'

import { SelectBox } from 'components/select-box'
import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
} from 'react'
import { useNavigate, Link } from 'react-router-dom'

import 'css/pages/settings.css'
import defaultPersonIcon from 'img/person.svg'

export const SettingsInput = ({
	label,
	name,
	value,
	onChange,
	format,
	to,
}) => {
	const inputRef = useRef()

	const [ editable, setEditable ] = useState(false)

	const voidFunc = () => {}

	const onBlur = async ({ target }) => {
		const formatted = format ? format(value) : value

		setEditable(false)

		if (!target.checkValidity()) {
			showToast(`Invalid value entered. ${target.validationMessage}`)
			return
		}

		const response = await updateUserDetails({ [name]: value })

		if (response.status !== 200) {
			showToast('Something went wrong. Please try again.')
			return
		}

		setCache({
			[name]: value,
		})
	}

	const onKeyPress = e => {
		if (e.key !== 'Enter') return
		
		e.target.blur()
	}

	useEffect(() => {
		editable && inputRef.current.focus()
	}, [editable])

	return (
		<div className="settings-item">
			<div className="field-label-container">
				<span className="field-label">{label}</span>
				<Link
					to={to}
					className="edit-link"
					onClick={() => setEditable(true)}
				>
					Edit
				</Link>
			</div>
			<input
				ref={inputRef}
				className={`field-value ${editable ? 'editable' : ''}`}
				name={name}
				value={(format ? format(value) : value) || ''}
				onChange={onChange || voidFunc}
				onBlur={onBlur}
				onKeyPress={onKeyPress}
			/>
		</div>
	)
}

export const SettingsInputViewOnly = ({
	label,
	name,
	value,
	format,
	to,
}) => {
	return (
			<div className="settings-item">
			<div className="field-label-container">
				<span className="field-label">{label}</span>
			</div>
			<input
				className={`field-value`}
				name={name}
				value={(format ? format(value) : value) || ''}
			/>
		</div>
	)
}

export const SettingsSelectBox = ({
	label,
	name,
	options,
	selected,
	onChange,
	onBlur,
}) => {
	const selectBoxRef = useRef()
	const [ expand, setExpand ] = useState()

	const handleClickAway = useCallback((e) => {
		if (selectBoxRef.current?.contains(e.target)) return

		setExpand(false)

	}, [])

	useEffect(() => {
		expand
			? document.addEventListener('click', handleClickAway)
			: document.removeEventListener('click', handleClickAway)

		if (expand !== false) return

		onBlur && onBlur(name, selected)
	}, [expand])

	return (
		<div className="settings-item" ref={selectBoxRef}>
			<div className="field-label-container">
				<span className="field-label">{label}</span>
				<Link
					className="edit-link"
					onClick={() => setExpand(true)}
				>
					Edit
				</Link>
			</div>
			<SelectBox
				name={name}
				expand={expand}
				options={options}
				selected={selected}
				onChange={onChange}
			/>
		</div>
	)
}

export const Settings = () => {
	const navigate = useNavigate()
	// `|| {}` is load-bearing: getCache returns undefined (not an empty
	// object) when NONE of the requested keys are in localStorage, which is
	// the real state for anyone who lands on /settings without having hit a
	// page that caches user details first -- a direct URL, a bookmark, or a
	// cleared browser. Without this, the `cachedVals.profilePicture` read
	// below throws and the root error boundary replaces the whole page with
	// "Oops! Something went wrong." Found by loading /settings in a fresh
	// browser session; it was reproducing on staging.
	const cachedVals = getCache([
		'profilePicture', 'firstName', 'lastName',
		'email', 'phoneNumber', 'accountType',
		'title', 'schoolOrOrg', 'gradesTaught',
		'subjectsTaught',
	]) || {}
	const selectImageRef = useRef()

	const [ userDetails, setUserDetails ] = useState({
		...cachedVals,
		profilePicture: cachedVals.profilePicture || defaultPersonIcon,
		gradesTaught: cachedVals.gradesTaught?.split(','),
		subjectsTaught: cachedVals.subjectsTaught?.split(','),
	})

	const formatPhoneNumber = phoneNumber => {
		const nums = (phoneNumber?.replace(/(^\+1|[^0-9])/g, '') || '')
			.padEnd(10, ' ')
		const formatted =
			`+1 (${nums.slice(0, 3)}) ${nums.slice(3, 6)}-${nums.slice(6, 10)}`

		return formatted
	}

	const initializeUserDetails = useCallback(async () => {
		const verified = verifyLogin(navigate)

		if (!verified) return

		// get() now throws on failure; without this catch the rejection is unhandled
		let userDetails
		try {
			userDetails = await getUserDetails()
		} catch (e) {
			return
		}

		setCache(userDetails)

		setUserDetails({
			...userDetails,
			gradesTaught: userDetails.gradesTaught,
			subjectsTaught: userDetails.subjectsTaught,
		})
	}, [])

	const onInputChange = ({ target }) => {
		const key = target.name
		const val = target.value

		setUserDetails(prev => ({ ...prev, [key]: val }))
	}

	const onSelectBoxChange = (name, selected) => {
		setUserDetails(prev => ({ ...prev, [name]: selected }))
	}

	const onSelectBoxBlur = async (name, selected) => {
		const response = await updateUserDetails({ [name]: selected })

		if (response.status !== 200) {
			showToast('Something went wrong. Please try again.')
			return
		}

		setCache({
			[name]: selected,
		})
	}

	const updateProfilePic = async ({ target }) => {
		const uploadResponse = await uploadFiles(target)

		if (uploadResponse.status !== 200) {
			showToast('Something went wrong. Please try again.')
			return
		}

		const { urls: [profilePicture] } = await uploadResponse.json()

		const updateDetailsResponse = await updateUserDetails({
			profilePicture,
		})

		if (updateDetailsResponse.status !== 200) {
			showToast('Something went wrong. Please try again.')
			return
		}

		setCache({ profilePicture })
		window.dispatchEvent(new CustomEvent('afo:profilePicUpdated'))
		setUserDetails(prev => ({
			...prev,
			profilePicture,
		}))
	}

	const removeProfilePic = async () => {
		// Use empty string to indicate no profile picture - navbar will use default person icon
		const updateDetailsResponse = await updateUserDetails({
			profilePicture: '',
		})

		if (updateDetailsResponse.status !== 200) {
			showToast('Something went wrong. Please try again.')
			return
		}

		setCache({ profilePicture: '' })
		window.dispatchEvent(new CustomEvent('afo:profilePicUpdated'))
		setUserDetails(prev => ({
			...prev,
			profilePicture: defaultPersonIcon,
		}))
	}

	useEffect(() => {
		initializeUserDetails()
	}, [])

	useDocTitle('All for One - Settings')

	return (
		<section className="settings-container">
			{/* mockup .page-head -- eyebrow above the title, centred */}
			<div className="settings-page-head">
				<span className="page-eyebrow">Your account</span>
				<h2 className="settings-heading">Settings</h2>
			</div>

			<div className="content-wrapper">
				<div className="profile-box">
					<div className="profile-picture-wrapper">
						<img
							id="profile-picture"
							alt="Profile Picture"
							src={userDetails.profilePicture || defaultPersonIcon}
						/>
						{userDetails.profilePicture &&
						 userDetails.profilePicture !== defaultPersonIcon &&
						 !userDetails.profilePicture.includes('person.svg') && (
							<button
								className="remove-picture-btn"
								onClick={removeProfilePic}
								title="Remove profile picture"
							>
								×
							</button>
						)}
					</div>
					<h3 id="profile-name">{userDetails.firstName}</h3>
					<input
						ref={selectImageRef}
						id="select-image"
						type="file"
						accept="image/*"
						style={{
							display: 'none'
						}}
						onChange={updateProfilePic}
					/>
					<button
						id="edit-profile-image"
						onClick={() => selectImageRef.current.click()}
					>
						Edit
					</button>
				</div>

				<div className="settings-fields">
				<SettingsInputViewOnly
					label="First name"
					name="firstName"
					value={userDetails.firstName}
				/>

				<SettingsInputViewOnly
					label="Last name"
					name="lastName"
					value={userDetails.lastName}
				/>

				<SettingsInputViewOnly
					label="Email"
					name="email"
					value={userDetails.email}
				/>

				<SettingsInput
					label="Phone number"
					name="phoneNumber"
					value={userDetails.phoneNumber}
					onChange={onInputChange}
					format={formatPhoneNumber}
				/>

				<SettingsInputViewOnly
					label="Account type"
					name="accountType"
					value={userDetails.accountType}
				/>

				<SettingsInput
					label="Title"
					name="title"
					value={userDetails.title}
					onChange={onInputChange}
				/>

				<SettingsInput
					label="School or organization"
					name="schoolOrOrg"
					value={userDetails.schoolOrOrg}
					onChange={onInputChange}
				/>

				<SettingsSelectBox
					label="Grades I teach"
					name="gradesTaught"
					options={[
						'Kindergarten', 'First Grade', 'Second Grade',
						'Third Grade', 'Fourth Grade', 'Fifth Grade',
						'Sixth Grade', 'Seventh Grade', 'Eighth Grade',
						'Ninth Grade', 'Tenth Grade', 'Eleventh Grade',
						'Twelfth Grade', 'Transition (18+)',
					]}
					selected={userDetails.gradesTaught}
					onChange={onSelectBoxChange}
					onBlur={onSelectBoxBlur}
				/>

				<SettingsSelectBox
					label="Subjects I teach"
					name="subjectsTaught"
					options={[
						'Science', 'Math', 'ELA',
						'Social Studies', 'Other',
					]}
					selected={userDetails.subjectsTaught}
					onChange={onSelectBoxChange}
					onBlur={onSelectBoxBlur}
				/>

				<SettingsInput
					label="Password"
					name="password"
					value={userDetails.password || '********'}
					to={'/forgot-password?from=settings'}
				/>
			</div>
		</div>
		</section>
	)
}
